import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ isCollapsed = false, onToggleSidebar }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState(3);
  const [userRole, setUserRole] = useState('admin');
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/admin-dashboard', 
      roles: ['admin', 'technician', 'client'],
      icon: 'LayoutDashboard'
    },
    { 
      label: 'Projetos', 
      path: '/project-management', 
      roles: ['admin'],
      icon: 'FolderOpen'
    },
    { 
      label: 'Ordens de Serviço', 
      path: '/service-orders', 
      roles: ['admin', 'technician'],
      icon: 'ClipboardList'
    },
    { 
      label: 'Portal do Cliente', 
      path: '/client-portal', 
      roles: ['admin', 'client'],
      icon: 'Users'
    },
    { 
      label: 'Mobile Técnico', 
      path: '/technician-mobile', 
      roles: ['technician'],
      icon: 'Smartphone'
    }
  ];

  const visibleItems = navigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  )?.slice(0, 4);

  const overflowItems = navigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  )?.slice(4);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    navigate('/login');
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-border shadow-professional">
        <div className="flex items-center justify-between h-20 px-4 lg:px-6">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-4">
            {onToggleSidebar && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleSidebar}
                className="lg:hidden"
              >
                <Icon name="Menu" size={24} />
              </Button>
            )}
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Briefcase" size={24} color="white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-semibold text-primary">ConsultFlow</h1>
                <p className="text-xs text-text-secondary font-medium">Pro</p>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {visibleItems?.map((item) => (
              <Button
                key={item?.path}
                variant={location?.pathname === item?.path ? "default" : "ghost"}
                onClick={() => handleNavigation(item?.path)}
                className="relative"
              >
                <Icon name={item?.icon} size={18} className="mr-2" />
                {item?.label}
                {item?.path === '/service-orders' && notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs rounded-full flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </Button>
            ))}
            
            {overflowItems?.length > 0 && (
              <div className="relative group">
                <Button variant="ghost">
                  <Icon name="MoreHorizontal" size={18} className="mr-2" />
                  Mais
                </Button>
                <div className="absolute top-full right-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-60">
                  {overflowItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className="w-full flex items-center px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors"
                    >
                      <Icon name={item?.icon} size={16} className="mr-3" />
                      {item?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-3">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative">
              <Icon name="Bell" size={20} />
              {notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs rounded-full flex items-center justify-center">
                  {notifications}
                </span>
              )}
            </Button>

            {/* User Menu */}
            <div className="relative group">
              <Button variant="ghost" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">Admin User</p>
                  <p className="text-xs text-text-secondary capitalize">{userRole}</p>
                </div>
                <Icon name="ChevronDown" size={16} />
              </Button>
              
              <div className="absolute top-full right-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-60">
                <button className="w-full flex items-center px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors">
                  <Icon name="Settings" size={16} className="mr-3" />
                  Configurações
                </button>
                <button className="w-full flex items-center px-4 py-3 text-sm text-popover-foreground hover:bg-muted transition-colors">
                  <Icon name="HelpCircle" size={16} className="mr-3" />
                  Ajuda
                </button>
                <hr className="border-border" />
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-3 text-sm text-error hover:bg-muted transition-colors"
                >
                  <Icon name="LogOut" size={16} className="mr-3" />
                  Sair
                </button>
              </div>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-surface border-t border-border animate-slide-in">
            <nav className="px-4 py-4 space-y-2">
              {navigationItems?.filter(item => item?.roles?.includes(userRole))?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-colors touch-target ${
                    location?.pathname === item?.path
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} className="mr-3" />
                  {item?.label}
                  {item?.path === '/service-orders' && notifications > 0 && (
                    <span className="ml-auto w-5 h-5 bg-error text-white text-xs rounded-full flex items-center justify-center">
                      {notifications}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>
      {/* Spacer for fixed header */}
      <div className="h-20"></div>
    </>
  );
};

export default Header;